import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { menuApi } from '../../services/api';
import { SkeletonCard } from '../../components/common/Skeleton';

const PLACEHOLDER = 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=200&q=80';

const ItemModal = ({ item, categories, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: item?.name || '',
    description: item?.description || '',
    price: item?.price || '',
    category_id: item?.category_id || '',
    is_available: item?.is_available ?? true,
    is_featured: item?.is_featured ?? false,
    is_special: item?.is_special ?? false,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (imageFile) fd.append('image', imageFile);

      if (item?.id) {
        await menuApi.update(item.id, fd);
        toast.success('Menu item updated!');
      } else {
        await menuApi.create(fd);
        toast.success('Menu item created!');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const inp = {
    width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 14,
    border: '1.5px solid #e8d5c4', background: '#fffaf7',
    color: '#1a0f00', fontFamily: 'inherit', outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  };
  const Label = ({ children }) => (
    <label style={{ display: 'block', color: '#6b4c35', fontSize: 11, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {children}
    </label>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: 'absolute', inset: 0, background: 'rgba(26,15,0,0.65)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.93, y: 24 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.93 }}
        style={{ position: 'relative', background: '#fff', borderRadius: 22, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 32px 80px rgba(0,0,0,0.2)', border: '1px solid #f0e4d8' }}>

        {/* Header */}
        <div style={{ padding: '22px 24px 18px', borderBottom: '1px solid #f5ede4', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, #fff8f3, #fff)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#c96428,#9a4a1e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, boxShadow: '0 4px 12px rgba(201,100,40,0.3)' }}>
              {item ? '✏️' : '➕'}
            </div>
            <div>
              <h2 style={{ fontFamily: '"Playfair Display",serif', fontSize: 18, fontWeight: 700, color: '#1a0f00', margin: 0 }}>{item ? 'Edit Item' : 'Add Menu Item'}</h2>
              <p style={{ fontSize: 12, color: '#8b6347', margin: 0 }}>{item ? 'Update menu item details' : 'Add a new item to menu'}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: 10, border: '1px solid #e8d5c4', background: '#fff', cursor: 'pointer', fontSize: 16, color: '#8b6347', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* Image upload */}
          <div>
            <Label>Image</Label>
            <div
              onClick={() => fileRef.current.click()}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              style={{ border: `2px dashed ${isDragging ? '#c96428' : '#e8d5c4'}`, borderRadius: 14, overflow: 'hidden', cursor: 'pointer', transition: 'all 0.2s', background: isDragging ? '#fff4ee' : '#fffaf7' }}>
              {imagePreview || item?.image_url ? (
                <div style={{ position: 'relative' }}>
                  <img src={imagePreview || (item?.image_url ? (item.image_url.startsWith('http') ? item.image_url : `http://localhost:5000${item.image_url}`) : PLACEHOLDER)} alt="Preview"
                    style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
                    onError={e => { e.target.src = PLACEHOLDER; }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}
                    onMouseOver={e => e.currentTarget.style.opacity = 1}
                    onMouseOut={e => e.currentTarget.style.opacity = 0}>
                    <p style={{ color: 'white', fontSize: 13, fontWeight: 600 }}>🔄 Click or drag to change</p>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '32px 0', textAlign: 'center' }}>
                  <div style={{ fontSize: 40, marginBottom: 8 }}>{isDragging ? '📂' : '📷'}</div>
                  <p style={{ color: isDragging ? '#c96428' : '#b08060', fontSize: 13, margin: 0, fontWeight: isDragging ? 700 : 400 }}>
                    {isDragging ? 'Drop image here!' : 'Click or drag & drop image'}
                  </p>
                  <p style={{ color: '#c8a890', fontSize: 11, margin: '4px 0 0' }}>PNG, JPG up to 5MB</p>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImage} />
          </div>

          {/* Name */}
          <div>
            <Label>Item Name *</Label>
            <input required placeholder="e.g. Latte Macchiato" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              style={inp}
              onFocus={e => { e.target.style.borderColor = '#c96428'; e.target.style.boxShadow = '0 0 0 3px rgba(201,100,40,0.1)'; }}
              onBlur={e => { e.target.style.borderColor = '#e8d5c4'; e.target.style.boxShadow = 'none'; }} />
          </div>

          {/* Description */}
          <div>
            <Label>Description</Label>
            <textarea rows={2} placeholder="Brief description of the item..." value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              style={{ ...inp, resize: 'none', lineHeight: 1.5 }}
              onFocus={e => { e.target.style.borderColor = '#c96428'; e.target.style.boxShadow = '0 0 0 3px rgba(201,100,40,0.1)'; }}
              onBlur={e => { e.target.style.borderColor = '#e8d5c4'; e.target.style.boxShadow = 'none'; }} />
          </div>

          {/* Price + Category */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <Label>Price (₹) *</Label>
              <input type="number" step="0.01" min="0" required placeholder="0.00" value={form.price}
                onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                style={inp}
                onFocus={e => { e.target.style.borderColor = '#c96428'; e.target.style.boxShadow = '0 0 0 3px rgba(201,100,40,0.1)'; }}
                onBlur={e => { e.target.style.borderColor = '#e8d5c4'; e.target.style.boxShadow = 'none'; }} />
            </div>
            <div>
              <Label>Category</Label>
              <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
                style={inp}
                onFocus={e => { e.target.style.borderColor = '#c96428'; e.target.style.boxShadow = '0 0 0 3px rgba(201,100,40,0.1)'; }}
                onBlur={e => { e.target.style.borderColor = '#e8d5c4'; e.target.style.boxShadow = 'none'; }}>
                <option value="">None</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          {/* Toggles */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            {[
              { key: 'is_available', label: 'Available', icon: '✅', color: '#22c55e' },
              { key: 'is_featured',  label: 'Featured',  icon: '⭐', color: '#f59e0b' },
              { key: 'is_special',   label: 'Special',   icon: '🔥', color: '#c96428' },
            ].map(({ key, label, icon, color }) => (
              <div key={key} onClick={() => setForm(f => ({ ...f, [key]: !f[key] }))}
                style={{ padding: '10px 12px', borderRadius: 12, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s', border: `2px solid ${form[key] ? color : '#e8d5c4'}`, background: form[key] ? `${color}15` : '#fffaf7' }}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: form[key] ? color : '#8b6347' }}>{label}</div>
                <div style={{ fontSize: 10, color: form[key] ? color : '#b08060', marginTop: 2 }}>{form[key] ? 'ON' : 'OFF'}</div>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 12, paddingTop: 4 }}>
            <button type="button" onClick={onClose}
              style={{ flex: 1, padding: '12px 0', borderRadius: 12, border: '1.5px solid #e8d5c4', background: '#fff', color: '#6b4c35', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
              onMouseOver={e => e.target.style.background = '#fff8f3'}
              onMouseOut={e => e.target.style.background = '#fff'}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              style={{ flex: 2, padding: '12px 0', borderRadius: 12, border: 'none', background: saving ? '#e8d5c4' : 'linear-gradient(135deg,#c96428,#9a4a1e)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', boxShadow: saving ? 'none' : '0 4px 16px rgba(201,100,40,0.35)' }}>
              {saving ? '⏳ Saving...' : item ? '✅ Update Item' : '➕ Add Item'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const AdminMenu = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'add' | item object
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [filterCat, setFilterCat] = useState('');
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [savingCat, setSavingCat] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name'); // name | price_asc | price_desc | available

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [menuRes, catRes] = await Promise.all([menuApi.getAll({ limit: 200 }), menuApi.getCategories()]);
      setItems(menuRes.data.data || []);
      setCategories(catRes.data.data || []);
    } catch { toast.error('Failed to load menu'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return toast.error('Category name required');
    setSavingCat(true);
    try {
      await menuApi.createCategory({ name: newCatName.trim() });
      toast.success('Category added!');
      setNewCatName('');
      setShowAddCat(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add category');
    } finally { setSavingCat(false); }
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? This will fail if items exist in this category.`)) return;
    try {
      const token = localStorage.getItem("cafe_admin_token");
      const res = await fetch(`http://localhost:5000/api/menu/categories/${cat.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) { alert(data.message || "Failed to delete"); return; }
      setCategories(prev => prev.filter(c => c.id !== cat.id));
      if (filterCat === String(cat.id)) setFilterCat("");
    } catch { alert("Server error"); }
  };

  const handleDelete = async (id) => {
    try {
      await menuApi.delete(id);
      toast.success('Item deleted');
      setDeleteConfirm(null);
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = items
    .filter(i => !filterCat || i.category_id === parseInt(filterCat))
    .filter(i => !search.trim() || i.name.toLowerCase().includes(search.trim().toLowerCase()) || (i.description||'').toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'price_asc')  return parseFloat(a.price) - parseFloat(b.price);
      if (sortBy === 'price_desc') return parseFloat(b.price) - parseFloat(a.price);
      if (sortBy === 'available')  return (b.is_available ? 1 : 0) - (a.is_available ? 1 : 0);
      return a.name.localeCompare(b.name);
    });

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 style={{color:"#1a0f00", fontFamily:'Playfair Display, serif', fontSize:24, fontWeight:700, margin:0}}>Menu Management</h1>
          <p className="text-bark-400 mt-1">{filtered.length} of {items.length} items</p>
        </div>
        <div style={{display:'flex', gap:10}}>
          <button onClick={() => setShowAddCat(true)}
            style={{padding:'9px 16px', borderRadius:10, border:'1px solid #e2d5c8', background:'white', color:'#8b6347', fontSize:13, fontWeight:600, fontFamily:'inherit', cursor:'pointer'}}>
            + Add Category
          </button>
          <button onClick={() => setModal('add')} className="flex items-center gap-2 bg-espresso-500 hover:bg-espresso-600 text-white px-5 py-2.5 rounded-xl font-medium transition-colors text-sm">
            + Add Item
          </button>
        </div>
      </div>

      {/* Search + Sort */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍  Search items..."
          style={{ flex:1, minWidth:200, padding:'10px 16px', borderRadius:12, border:'1.5px solid #e8d5c4', background:'#fffaf7', color:'#1a0f00', fontSize:13, fontFamily:'inherit', outline:'none' }}
        />
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          style={{ padding:'10px 14px', borderRadius:12, border:'1.5px solid #e8d5c4', background:'#fffaf7', color:'#6b4c35', fontSize:13, fontFamily:'inherit', outline:'none', cursor:'pointer' }}>
          <option value="name">Sort: A → Z</option>
          <option value="price_asc">Sort: Price Low → High</option>
          <option value="price_desc">Sort: Price High → Low</option>
          <option value="available">Sort: Available First</option>
        </select>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {/* All button */}
        <button onClick={() => setFilterCat('')} style={{
          padding: '7px 18px', borderRadius: 99, fontSize: 12, fontWeight: 700,
          fontFamily: 'inherit', cursor: 'pointer', transition: 'all 0.15s',
          background: !filterCat ? 'linear-gradient(135deg,#c96428,#9a4a1e)' : '#fff',
          color: !filterCat ? '#fff' : '#6b4c35',
          border: !filterCat ? 'none' : '1.5px solid #e8d5c4',
          boxShadow: !filterCat ? '0 2px 8px rgba(201,100,40,0.3)' : 'none',
        }}>All</button>

        {categories.map(c => {
          const active = filterCat === String(c.id);
          return (
            <div key={c.id} style={{ display:'inline-flex', alignItems:'center', borderRadius:99, overflow:'hidden', border: active ? 'none' : '1.5px solid #e8d5c4', boxShadow: active ? '0 2px 8px rgba(201,100,40,0.3)' : 'none', background: active ? 'linear-gradient(135deg,#c96428,#9a4a1e)' : '#fff', transition:'all 0.15s' }}>
              <button onClick={() => setFilterCat(String(c.id))} style={{
                padding: '7px 14px 7px 16px', fontSize: 12, fontWeight: 700,
                fontFamily: 'inherit', cursor: 'pointer', background: 'transparent',
                border: 'none', color: active ? '#fff' : '#6b4c35',
              }}>
                {c.name}
              </button>
              <button onClick={() => handleDeleteCategory(c)} title="Delete category" style={{
                padding: '4px 10px 4px 4px', background: 'transparent', border: 'none',
                cursor: 'pointer', color: active ? 'rgba(255,255,255,0.8)' : '#ef4444',
                fontSize: 11, fontWeight: 700, lineHeight: 1,
              }}>✕</button>
            </div>
          );
        })}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array(8).fill(0).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(item => (
            <motion.div key={item.id} layout
              whileHover={{ y: -4, boxShadow: '0 12px 40px rgba(201,100,40,0.18)' }}
              transition={{ duration: 0.2 }}
              style={{ background: '#fff', borderRadius: 18, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.08)', border: '1px solid #f0e8df', cursor: 'pointer' }}>
              {/* Image */}
              <div style={{ position: 'relative', height: 180, background: '#f5ede4', overflow: 'hidden' }}>
                <img
                  src={item.image_url ? (item.image_url.startsWith('http') ? item.image_url : `http://localhost:5000${item.image_url}`) : PLACEHOLDER}
                  alt={item.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }}
                  onMouseOver={e => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={e => e.target.style.transform = 'scale(1)'}
                  onError={e => { e.target.src = PLACEHOLDER; }}
                />
                {/* Gradient overlay */}
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 60%)' }}/>
                {/* Badges */}
                <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 6 }}>
                  {item.is_featured && (
                    <span style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99, display: 'flex', alignItems: 'center', gap: 3, boxShadow: '0 2px 8px rgba(245,158,11,0.4)' }}>
                      ⭐ Featured
                    </span>
                  )}
                  {item.is_special && (
                    <span style={{ background: 'linear-gradient(135deg,#c96428,#9a4a1e)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99, display: 'flex', alignItems: 'center', gap: 3, boxShadow: '0 2px 8px rgba(201,100,40,0.4)' }}>
                      🔥 Special
                    </span>
                  )}
                </div>
                {/* Unavailable overlay */}
                {!item.is_available && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 99 }}>Unavailable</span>
                  </div>
                )}
                {/* Price on image */}
                <div style={{ position: 'absolute', bottom: 10, right: 10, background: 'rgba(201,100,40,0.92)', color: '#fff', fontWeight: 800, fontSize: 14, padding: '4px 10px', borderRadius: 99, backdropFilter: 'blur(4px)' }}>
                  ₹{parseFloat(item.price).toFixed(0)}
                </div>
              </div>
              {/* Info */}
              <div style={{ padding: '14px 16px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ fontFamily: '"Playfair Display",serif', fontSize: 15, fontWeight: 700, color: '#1a0f00', margin: 0, textTransform: 'capitalize', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</h3>
                    <span style={{ display: 'inline-block', marginTop: 4, fontSize: 10, fontWeight: 600, color: '#c96428', background: '#fff4ee', border: '1px solid #fdd8c0', borderRadius: 99, padding: '2px 8px', textTransform: 'capitalize' }}>
                      {item.category_name || 'Uncategorized'}
                    </span>
                  </div>
                </div>
                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button onClick={() => setModal(item)}
                    style={{ flex: 1, padding: '7px 0', borderRadius: 10, fontSize: 12, fontWeight: 700, border: '1.5px solid #c96428', background: '#fff', color: '#c96428', cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit' }}
                    onMouseOver={e => { e.target.style.background = '#c96428'; e.target.style.color = '#fff'; }}
                    onMouseOut={e => { e.target.style.background = '#fff'; e.target.style.color = '#c96428'; }}>
                    ✏️ Edit
                  </button>
                  <button onClick={() => setDeleteConfirm(item.id)}
                    style={{ flex: 1, padding: '7px 0', borderRadius: 10, fontSize: 12, fontWeight: 700, border: '1.5px solid #fee2e2', background: '#fff5f5', color: '#ef4444', cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit' }}
                    onMouseOver={e => { e.target.style.background = '#ef4444'; e.target.style.color = '#fff'; }}
                    onMouseOut={e => { e.target.style.background = '#fff5f5'; e.target.style.color = '#ef4444'; }}>
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {modal && (
          <ItemModal
            item={modal === 'add' ? null : modal}
            categories={categories}
            onClose={() => setModal(null)}
            onSaved={fetchAll}
          />
        )}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60" onClick={() => setDeleteConfirm(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              className="relative bg-bark-900 border border-bark-800 rounded-2xl p-6 w-full max-w-sm shadow-2xl text-center">
              <div className="text-4xl mb-4">🗑️</div>
              <h3 className="font-display text-lg font-bold text-orange-200 mb-2">Delete Item?</h3>
              <p className="text-bark-400 text-sm mb-6">This action cannot be undone.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-xl border border-bark-700 text-bark-300 hover:bg-bark-800 transition-colors text-sm">Cancel</button>
                <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors">Delete</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add Category Modal */}
      {showAddCat && (
        <div style={{position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:16}}>
          <div onClick={() => setShowAddCat(false)} style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.35)'}} />
          <div style={{position:'relative', background:'white', borderRadius:20, padding:28, width:'100%', maxWidth:380, boxShadow:'0 8px 40px rgba(0,0,0,0.15)', border:'1px solid #e8ddd3'}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20}}>
              <h2 style={{fontFamily:'Playfair Display,serif', fontSize:18, fontWeight:700, color:'#1a0f00', margin:0}}>Add New Category</h2>
              <button onClick={() => setShowAddCat(false)} style={{background:'#f3ede6', border:'none', borderRadius:8, width:32, height:32, cursor:'pointer', color:'#8b6347', fontSize:16}}>✕</button>
            </div>
            <div style={{marginBottom:16}}>
              <label style={{display:'block', color:'#8b6347', fontSize:12, fontWeight:600, marginBottom:7, textTransform:'uppercase', letterSpacing:0.5}}>Category Name *</label>
              <input
                type="text"
                placeholder="e.g. Beverages, Salads..."
                value={newCatName}
                onChange={e => setNewCatName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
                style={{width:'100%', padding:'12px 14px', borderRadius:10, border:'1.5px solid #e8ddd3', background:'#f8f5f1', color:'#1a0f00', fontSize:14, fontFamily:'inherit', outline:'none'}}
                autoFocus
              />
            </div>
            <div style={{display:'flex', gap:10}}>
              <button onClick={() => { setShowAddCat(false); setNewCatName(''); }}
                style={{flex:1, padding:'11px 20px', borderRadius:10, border:'1px solid #e8ddd3', background:'#f3ede6', color:'#8b6347', fontSize:14, fontWeight:500, fontFamily:'inherit', cursor:'pointer'}}>
                Cancel
              </button>
              <button onClick={handleAddCategory} disabled={savingCat}
                style={{flex:1, padding:'11px 20px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#c96428,#a84e1e)', color:'white', fontSize:14, fontWeight:600, fontFamily:'inherit', cursor:'pointer', opacity:savingCat?0.7:1}}>
                {savingCat ? 'Adding...' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMenu;
